import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Collapse, Stack } from '@mui/material';
import { getCookie } from 'cookies-next';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import NumberFormat from 'react-number-format';
import texts from './texts';
import { FormModel, FormSchema } from './validation';
import { SendMobileOtp, SendOtp } from '@/api/hooks/auth/types';
import { useSendMobileOtp } from '@/api/hooks/auth/useSendMobileOtp';
import LoadingButton from '@/components/LoadingButton';
import { ArrowDropDown, ArrowDropUp } from '@/components/svg-icons';
import FormTitle from '@/containers/auth/components/FormTitle';
import { TextField } from '@/ui/kit/components/textfield';
import { ROUTES, SESSION_KEYS } from '@/utils/constants';
import { get2MinuetsLaterInTimestamp } from '@/utils/function';
import { sendGoogleGTMEvent } from '@/utils/googleGTM';
const MobileFrom = () => {
  const authKeys = SESSION_KEYS.auth;
  const [showReferral, setShowReferral] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralCode = searchParams.get('ref') ?? getCookie('referral_code');
  const {
    control,
    handleSubmit,
    setValue
  } = useForm<FormModel>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      mobile_number: '',
      referral_code: ''
    }
  });
  const handleOTPSent = (data: SendMobileOtp['Response'], params: SendMobileOtp['Payload']) => {
    sessionStorage.setItem(authKeys.mobileNumber, params.mobile_number);
    sessionStorage.setItem(authKeys.otpTimeout, get2MinuetsLaterInTimestamp().toString());
    sessionStorage.setItem(authKeys.obfuscationMobileNumber, data.mobile_number);
    router.push(ROUTES.mobileOtpVerification.href);
  };
  const handleSendOtpError = (error: SendOtp['Error']) => {
    if (error.error_code === 'previous_otp_has_not_expired') {
      router.push(ROUTES.mobileOtpVerification.href);
    }
  };
  const {
    mutate,
    isPending
  } = useSendMobileOtp({
    onSuccess: handleOTPSent,
    onError: handleSendOtpError
  });
  const onSubmit: SubmitHandler<FormModel> = (data: FormModel) => {
    if (data.referral_code) {
      sessionStorage.setItem(authKeys.referralCode, data.referral_code);
    }
    sendGoogleGTMEvent({
      event: 'log_reg_checkotp'
    });
    mutate({
      mobile_number: data.mobile_number
    });
  };
  useEffect(() => {
    if (referralCode) {
      setValue('referral_code', referralCode);
      setShowReferral(true);
    }
  }, [referralCode]);
  return <Stack component='form' gap='var(--spacing_l)' width='100%' onSubmit={handleSubmit(onSubmit)} noValidate data-sentry-element="Stack" data-sentry-component="MobileFrom" data-sentry-source-file="index.tsx">
      <FormTitle title={texts.title} data-sentry-element="FormTitle" data-sentry-source-file="index.tsx" />
      <Controller control={control} name='mobile_number' render={({
      field,
      fieldState
    }) => <NumberFormat type='tel' customInput={TextField} name={field.name} disabled={isPending} label={texts.form.mobileNumber.label} placeholder={texts.form.mobileNumber.placeholder} error={fieldState.invalid} helperText={fieldState.error?.message} value={field.value ?? ''} onChange={field.onChange} size='large' inputProps={{
      maxLength: 11
    }} sx={{
      textAlign: 'left',
      direction: 'ltr'
    }} customNumerals={['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']} isNumericString />} data-sentry-element="Controller" data-sentry-source-file="index.tsx" />
      <Stack spacing={1} width={1} alignItems='flex-start' data-sentry-element="Stack" data-sentry-source-file="index.tsx">
        <Button startIcon={showReferral ? <ArrowDropUp /> : <ArrowDropDown />} variant='text' onClick={() => setShowReferral(prv => !prv)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {texts.form.referralCode.label}
        </Button>
        <Collapse in={showReferral} sx={{
        width: 1
      }} data-sentry-element="Collapse" data-sentry-source-file="index.tsx">
          <Controller control={control} name='referral_code' render={({
          field,
          fieldState
        }) => <TextField name={field.name} disabled={isPending} error={fieldState.invalid} helperText={fieldState.error?.message} value={field.value ?? ''} onChange={field.onChange} size='large' inputProps={{
          maxLength: 8
        }} fullWidth sx={{
          textAlign: 'left',
          direction: 'ltr',
          m: 0
        }} />} data-sentry-element="Controller" data-sentry-source-file="index.tsx" />
        </Collapse>
      </Stack>

      <LoadingButton type='submit' variant='contained' size='large' loading={isPending} disabled={isPending} fullWidth data-sentry-element="LoadingButton" data-sentry-source-file="index.tsx">
        {texts.form.button}
      </LoadingButton>
    </Stack>;
};
export default MobileFrom;